import { PopularEvent } from "@playarena/shared";
import {
  Calendar,
  Ticket,
  Star,
  TrendingUp,
  Clock,
  MapPin,
  Heart,
  Activity,
  Trophy,
  DollarSign,
  Target,
  CheckCircle,
  AlertCircle,
  Plus,
  Eye,
  BookOpen,
} from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGetFanAnalyticsQuery,
  useGetBookingStatsQuery,
  useGetDashboardStatsQuery,
  useGetRecentActivityQuery,
  useTrackEventMutation,
} from "../../../store/api/analyticsApi";
import { useGetMyBookingsQuery } from "../../../store/api/bookingApi";
import { useGetUpcomingEventsQuery, useGetEventsQuery } from "../../../store/api/eventApi";
import { formatCurrency, formatDateTime, capitalizeFirst } from "../../../utils/helpers";

const FanDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("overview");

  // Fetch analytics data
  const { data: fanAnalyticsData, isLoading: fanAnalyticsLoading } = useGetFanAnalyticsQuery();
  const { data: bookingStatsData, isLoading: bookingStatsLoading } = useGetBookingStatsQuery({});
  const { data: dashboardData } = useGetDashboardStatsQuery();
  const { data: recentActivityData } = useGetRecentActivityQuery({ limit: 10 });

  // Fetch user-specific data
  const { data: bookingsData } = useGetMyBookingsQuery({ limit: 10 });
  const { data: upcomingEventsData } = useGetUpcomingEventsQuery({ limit: 6 });
  const { data: recommendedEventsData } = useGetEventsQuery({ limit: 5 });

  // Track events mutation
  const [trackEvent] = useTrackEventMutation();

  const fanAnalytics = fanAnalyticsData?.data;
  const bookingStats = bookingStatsData?.data;
  const dashboardStats = dashboardData?.data;
  const recentActivities = recentActivityData?.data || [];
  const bookings = bookingsData?.data || [];
  const upcomingEvents = upcomingEventsData?.data || [];
  const recommendedEvents = recommendedEventsData?.data || [];

  // Track dashboard view
  useEffect(() => {
    if (user?.id) {
      trackEvent({
        type: "system_health",
        metadata: {
          userRole: "fan",
          action: "dashboard_viewed",
          timestamp: new Date(),
          userId: user.id,
        },
      });
    }
  }, [user?.id, trackEvent]);

  if (fanAnalyticsLoading || bookingStatsLoading) {
    return (
      <div className='container py-8'>
        <div className='animate-pulse'>
          <div className='mb-4 h-8 w-1/3 rounded bg-gray-200'></div>
          <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
            {[...Array(4)].map((_, i) => (
              <div key={i} className='h-24 rounded bg-gray-200'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Calculate stats from booking data
  const totalBookings = bookingStats?.totalBookings || bookings.length || 0;
  const confirmedBookings =
    bookingStats?.confirmedBookings || bookings.filter(b => b.status === "confirmed").length || 0;
  const completedBookings =
    bookingStats?.completedBookings || bookings.filter(b => b.status === "completed").length || 0;
  const totalSpent =
    bookingStats?.totalRevenue || bookings.reduce((sum, b) => sum + b.totalAmount, 0) || 0;
  const avgBookingValue =
    bookingStats?.averageBookingValue || (totalBookings > 0 ? totalSpent / totalBookings : 0);

  return (
    <div className='container py-8'>
      <div className='mb-8'>
        <h1 className='mb-2 text-3xl font-bold'>Welcome back, {user?.profile?.name}!</h1>
        <p className='text-muted-foreground'>
          Here&apos;s your booking overview and recommendations.
        </p>
      </div>

      {/* Enhanced Stats Cards */}
      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Bookings</CardTitle>
            <Ticket className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{totalBookings}</div>
            <p className='text-xs text-muted-foreground'>{confirmedBookings} confirmed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Upcoming Events</CardTitle>
            <Calendar className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{upcomingEvents.length}</div>
            <p className='text-xs text-muted-foreground'>Events you&apos;ve booked</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Events Attended</CardTitle>
            <Trophy className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{completedBookings}</div>
            <p className='text-xs text-muted-foreground'>Successfully attended</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Spent</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{formatCurrency(totalSpent)}</div>
            <p className='text-xs text-muted-foreground'>Avg: {formatCurrency(avgBookingValue)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className='space-y-6'>
        <TabsList>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='bookings'>My Bookings</TabsTrigger>
          <TabsTrigger value='upcoming'>Upcoming Events</TabsTrigger>
          <TabsTrigger value='recommendations'>For You</TabsTrigger>
          <TabsTrigger value='analytics'>My Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='space-y-6'>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {/* Recent Activity Card */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  {recentActivities.length > 0 ? (
                    recentActivities.slice(0, 5).map((activity: any, index: number) => (
                      <div key={index} className='flex items-center justify-between'>
                        <div>
                          <h4 className='font-medium'>
                            {capitalizeFirst(activity.type.replace(/_/g, " "))}
                          </h4>
                          <p className='text-sm text-muted-foreground'>
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                        <Badge
                          variant={
                            activity.type.includes("booking")
                              ? "default"
                              : activity.type.includes("error")
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {activity.type.includes("error") ? "Error" : "Success"}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className='py-4 text-center text-muted-foreground'>
                      No recent activities
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <Button className='w-full justify-start bg-transparent' variant='outline' asChild>
                  <Link to='/events'>
                    <Plus className='mr-2 h-4 w-4' />
                    Browse Events
                  </Link>
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline' asChild>
                  <Link to='/bookings'>
                    <Ticket className='mr-2 h-4 w-4' />
                    View My Bookings
                  </Link>
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline' asChild>
                  <Link to='/profile'>
                    <Star className='mr-2 h-4 w-4' />
                    Update Profile
                  </Link>
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline'>
                  <BookOpen className='mr-2 h-4 w-4' />
                  Booking History
                </Button>
              </CardContent>
            </Card>

            {/* Upcoming Events Preview */}
            <Card className='lg:col-span-2'>
              <CardHeader>
                <CardTitle className='flex items-center justify-between'>
                  <span>Your Upcoming Events</span>
                  <Button variant='outline' size='sm' asChild>
                    <Link to='/events'>View All</Link>
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingEvents.length > 0 ? (
                  <div className='space-y-3'>
                    {upcomingEvents.slice(0, 3).map((event: any) => (
                      <div
                        key={event.id}
                        className='flex items-center justify-between rounded-lg border p-3'
                      >
                        <div className='flex-1'>
                          <h4 className='font-medium text-primary transition-colors hover:text-secondary'>
                            {event.title}
                          </h4>
                          <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                            <div className='flex items-center gap-1'>
                              <Calendar className='h-3 w-3' />
                              <span>{formatDateTime(event.datetime)}</span>
                            </div>
                            <div className='flex items-center gap-1'>
                              <MapPin className='h-3 w-3' />
                              <span>{event.ground?.location.city}</span>
                            </div>
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          <Badge variant='outline'>
                            <Clock className='mr-1 h-3 w-3' />
                            Upcoming
                          </Badge>
                          <Button size='sm' variant='outline' asChild>
                            <Link to={`/events/${event.id}`}>
                              <Eye className='h-4 w-4' />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='py-6 text-center'>
                    <Calendar className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                    <p className='mb-4 text-muted-foreground'>No upcoming events</p>
                    <Button asChild>
                      <Link to='/events'>Find Events</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value='bookings' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>My Bookings ({bookings.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {bookings.length > 0 ? (
                <div className='space-y-4'>
                  {bookings.map(booking => (
                    <div
                      key={booking.id}
                      className='flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <h4 className='font-medium'>{booking.event?.title}</h4>
                        <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                          <div className='flex items-center gap-1'>
                            <Calendar className='h-3 w-3' />
                            <span>{formatDateTime(booking.event?.datetime || "")}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            <span>{booking.event?.ground?.location.city}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <Ticket className='h-3 w-3' />
                            <span>{booking.quantity} ticket(s)</span>
                          </div>
                        </div>
                        <div className='mt-2 flex items-center gap-2'>
                          <Badge variant='secondary' className='text-xs'>
                            {capitalizeFirst(booking.ticketType)}
                          </Badge>
                          <span className='text-xs text-muted-foreground'>
                            Ref: {booking.bookingReference}
                          </span>
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='font-medium'>{formatCurrency(booking.totalAmount)}</div>
                        <Badge
                          variant={
                            booking.status === "confirmed"
                              ? "default"
                              : booking.status === "pending"
                                ? "secondary"
                                : booking.status === "completed"
                                  ? "default"
                                  : "destructive"
                          }
                        >
                          {booking.status === "completed" && (
                            <CheckCircle className='mr-1 h-3 w-3' />
                          )}
                          {booking.status === "cancelled" && (
                            <AlertCircle className='mr-1 h-3 w-3' />
                          )}
                          {capitalizeFirst(booking.status)}
                        </Badge>
                      </div>
                    </div>
                  ))}

                  {bookings.length > 5 && (
                    <div className='pt-4'>
                      <Button variant='outline' className='w-full' asChild>
                        <Link to='/bookings'>View All Bookings</Link>
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Ticket className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>No bookings yet</p>
                  <Button asChild>
                    <Link to='/events'>Browse Events</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Status Summary */}
          {bookingStats && (
            <div className='grid grid-cols-1 gap-4 md:grid-cols-4'>
              <Card>
                <CardContent className='pt-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-blue-600'>
                      {bookingStats.totalBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Total</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className='pt-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-green-600'>
                      {bookingStats.confirmedBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Confirmed</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className='pt-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-orange-600'>
                      {bookingStats.pendingBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Pending</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className='pt-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-red-600'>
                      {bookingStats.cancelledBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Cancelled</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Spending Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <TrendingUp className='h-5 w-5' />
                Spending Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-3'>
                <div className='flex justify-between'>
                  <span>Total Spent:</span>
                  <span className='font-medium text-green-600'>{formatCurrency(totalSpent)}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Average per Booking:</span>
                  <span className='font-medium'>{formatCurrency(avgBookingValue)}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Highest Booking:</span>
                  <span className='font-medium'>
                    {formatCurrency(
                      bookings.length > 0 ? Math.max(...bookings.map(b => b.totalAmount)) : 0
                    )}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Most Recent:</span>
                  <span className='font-medium'>
                    {bookings.length > 0
                      ? formatCurrency(bookings[0]?.totalAmount || 0)
                      : formatCurrency(0)}
                  </span>
                </div>

                {fanAnalytics?.spendingPattern && (
                  <div className='border-t pt-3'>
                    <h4 className='mb-2 text-sm font-medium'>Recent Spending</h4>
                    {fanAnalytics.spendingPattern.slice(-3).map((month: any, index: number) => (
                      <div key={index} className='flex justify-between text-sm'>
                        <span>{month.month}:</span>
                        <span className='font-medium'>{formatCurrency(month.revenue)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Activity Chart */}
          {bookingStats?.bookingsByMonth && bookingStats.bookingsByMonth.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Monthly Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {bookingStats.bookingsByMonth.slice(-6).map((month: any, index: number) => (
                    <div key={index} className='flex items-center justify-between'>
                      <span className='font-medium'>{month.month}</span>
                      <div className='flex items-center gap-4'>
                        <div className='flex items-center gap-1'>
                          <Activity className='h-3 w-3 text-blue-500' />
                          <span className='text-sm'>{month.count} bookings</span>
                        </div>
                        <span className='text-sm font-medium text-green-600'>
                          {formatCurrency(month.revenue)}
                        </span>
                        {month.growth !== undefined && (
                          <span
                            className={`text-xs ${
                              month.growth >= 0 ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {month.growth > 0 ? "+" : ""}
                            {month.growth?.toFixed(1) || 0}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Activity Summary */}
                <div className='mt-6 grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-3'>
                  <div className='text-center'>
                    <div className='text-lg font-bold text-blue-600'>
                      {bookingStats.bookingsByMonth.reduce(
                        (sum: number, month: any) => sum + month.count,
                        0
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>Total Bookings</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-lg font-bold text-green-600'>
                      {formatCurrency(
                        bookingStats.bookingsByMonth.reduce(
                          (sum: number, month: any) => sum + month.revenue,
                          0
                        )
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>Total Spent</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-lg font-bold text-purple-600'>
                      {(
                        bookingStats.bookingsByMonth.reduce(
                          (sum: number, month: any) => sum + month.count,
                          0
                        ) / Math.max(bookingStats.bookingsByMonth.length, 1)
                      ).toFixed(1)}
                    </div>
                    <p className='text-xs text-muted-foreground'>Avg per Month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personal Stats and Achievements */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Trophy className='h-5 w-5' />
                Your Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div className='rounded-lg bg-blue-50 p-4 text-center'>
                  <Trophy className='mx-auto mb-2 h-8 w-8 text-blue-600' />
                  <div className='text-xl font-bold text-blue-600'>{completedBookings}</div>
                  <p className='text-sm text-blue-800'>Events Completed</p>
                </div>

                <div className='rounded-lg bg-green-50 p-4 text-center'>
                  <Star className='mx-auto mb-2 h-8 w-8 text-green-600' />
                  <div className='text-xl font-bold text-green-600'>
                    {fanAnalytics?.favoriteEvents?.length || 0}
                  </div>
                  <p className='text-sm text-green-800'>Favorite Events</p>
                </div>

                <div className='rounded-lg bg-purple-50 p-4 text-center'>
                  <DollarSign className='mx-auto mb-2 h-8 w-8 text-purple-600' />
                  <div className='text-xl font-bold text-purple-600'>
                    {formatCurrency(totalSpent)}
                  </div>
                  <p className='text-sm text-purple-800'>Total Investment</p>
                </div>

                <div className='rounded-lg bg-orange-50 p-4 text-center'>
                  <Calendar className='mx-auto mb-2 h-8 w-8 text-orange-600' />
                  <div className='text-xl font-bold text-orange-600'>
                    {Math.ceil(
                      (totalBookings || 1) / Math.max(bookingStats?.bookingsByMonth?.length || 1, 1)
                    )}
                  </div>
                  <p className='text-sm text-orange-800'>Avg Bookings/Month</p>
                </div>
              </div>

              {/* Progress indicators */}
              <div className='mt-6 space-y-3'>
                <div>
                  <div className='flex justify-between text-sm'>
                    <span>Event Attendance Goal</span>
                    <span>{completedBookings}/10</span>
                  </div>
                  <div className='mt-1 h-2 rounded-full bg-gray-200'>
                    <div
                      className='h-2 rounded-full bg-green-500 transition-all duration-300'
                      style={{ width: `${Math.min((completedBookings / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className='flex justify-between text-sm'>
                    <span>Booking Streak</span>
                    <span>{Math.min(confirmedBookings, 5)}/5</span>
                  </div>
                  <div className='mt-1 h-2 rounded-full bg-gray-200'>
                    <div
                      className='h-2 rounded-full bg-blue-500 transition-all duration-300'
                      style={{ width: `${Math.min((confirmedBookings / 5) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Milestones */}
              <div className='mt-6'>
                <h4 className='mb-3 text-sm font-medium'>Milestones</h4>
                <div className='space-y-2'>
                  <div
                    className={`flex items-center gap-2 ${
                      totalBookings >= 1 ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <CheckCircle className='h-4 w-4' />
                    <span className='text-sm'>First Booking</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${
                      completedBookings >= 1 ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <CheckCircle className='h-4 w-4' />
                    <span className='text-sm'>First Event Attended</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${
                      totalBookings >= 5 ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <CheckCircle className='h-4 w-4' />
                    <span className='text-sm'>Regular Attendee (5+ bookings)</span>
                  </div>
                  <div
                    className={`flex items-center gap-2 ${
                      totalSpent >= 100 ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    <CheckCircle className='h-4 w-4' />
                    <span className='text-sm'>Invested {formatCurrency(100)}+</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insights and Tips */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Activity className='h-5 w-5' />
                Personalized Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                {/* Dynamic insights based on user data */}
                {bookingStats?.cancellationRate && bookingStats.cancellationRate > 20 && (
                  <div className='rounded-lg border border-yellow-200 bg-yellow-50 p-4'>
                    <div className='flex items-center gap-2'>
                      <AlertCircle className='h-4 w-4 text-yellow-600' />
                      <span className='text-sm font-medium text-yellow-800'>Tip</span>
                    </div>
                    <p className='mt-1 text-sm text-yellow-700'>
                      Your cancellation rate is {bookingStats.cancellationRate.toFixed(1)}%.
                      Consider booking closer to event dates to reduce cancellations.
                    </p>
                  </div>
                )}

                {avgBookingValue > 0 && avgBookingValue < 50 && (
                  <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                    <div className='flex items-center gap-2'>
                      <Star className='h-4 w-4 text-blue-600' />
                      <span className='text-sm font-medium text-blue-800'>Opportunity</span>
                    </div>
                    <p className='mt-1 text-sm text-blue-700'>
                      You might enjoy premium experiences! Consider upgrading to VIP tickets for a
                      more exclusive experience.
                    </p>
                  </div>
                )}

                {completedBookings >= 3 && (
                  <div className='rounded-lg border border-green-200 bg-green-50 p-4'>
                    <div className='flex items-center gap-2'>
                      <Trophy className='h-4 w-4 text-green-600' />
                      <span className='text-sm font-medium text-green-800'>Achievement</span>
                    </div>
                    <p className='mt-1 text-sm text-green-700'>
                      You&apos;re a regular event-goer! You&apos;ve successfully attended{" "}
                      {completedBookings || 0} events. Keep exploring new experiences!
                    </p>
                  </div>
                )}

                {upcomingEvents.length === 0 && totalBookings > 0 && (
                  <div className='rounded-lg border border-purple-200 bg-purple-50 p-4'>
                    <div className='flex items-center gap-2'>
                      <Calendar className='h-4 w-4 text-purple-600' />
                      <span className='text-sm font-medium text-purple-800'>Suggestion</span>
                    </div>
                    <p className='mt-1 text-sm text-purple-700'>
                      No upcoming events? Check out our latest featured events and book your next
                      adventure!
                    </p>
                    <Button size='sm' className='mt-2' asChild>
                      <Link to='/events'>Browse Events</Link>
                    </Button>
                  </div>
                )}

                {/* Default encouragement */}
                {totalBookings === 0 && (
                  <div className='rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 p-4'>
                    <div className='flex items-center gap-2'>
                      <Heart className='h-4 w-4 text-blue-600' />
                      <span className='text-sm font-medium text-blue-800'>Welcome!</span>
                    </div>
                    <p className='mt-1 text-sm text-blue-700'>
                      Welcome to PlayArena! Start your journey by exploring amazing events and
                      making your first booking. Adventure awaits!
                    </p>
                    <Button size='sm' className='mt-2' asChild>
                      <Link to='/events'>Discover Events</Link>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='upcoming' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Your Upcoming Events ({upcomingEvents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length > 0 ? (
                <div className='space-y-4'>
                  {upcomingEvents.map((event: any) => (
                    <div
                      key={event.id}
                      className='group flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <h4 className='font-medium text-primary transition-colors group-hover:text-secondary'>
                            {event.title}
                          </h4>
                          {event.featured && <Star className='h-4 w-4 text-yellow-500' />}
                        </div>
                        <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                          <div className='flex items-center gap-1'>
                            <Calendar className='h-3 w-3' />
                            <span>{formatDateTime(event.datetime)}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            <span>{event.ground?.location.city}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <Clock className='h-3 w-3' />
                            <span>{event.duration} min</span>
                          </div>
                        </div>
                        {event.tags && (
                          <div className='mt-2 flex gap-1'>
                            {event.tags.slice(0, 3).map((tag: string) => (
                              <Badge key={tag} variant='secondary' className='text-xs'>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='text-right'>
                        <div className='mb-2'>
                          <Badge variant='outline' className='text-green-600'>
                            <CheckCircle className='mr-1 h-3 w-3' />
                            Booked
                          </Badge>
                        </div>
                        <Button size='sm' variant='outline' asChild>
                          <Link to={`/events/${event.id}`}>View Details</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Clock className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>No upcoming events</p>
                  <Button asChild>
                    <Link to='/events'>Find Events</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='recommendations' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Heart className='h-5 w-5 text-red-500' />
                Recommended for You
              </CardTitle>
            </CardHeader>
            <CardContent>
              {fanAnalytics?.recommendations && fanAnalytics.recommendations.length > 0 ? (
                <div className='space-y-4'>
                  {fanAnalytics.recommendations.map((event: any) => (
                    <div
                      key={event.id}
                      className='flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <h4 className='font-medium'>{event.title}</h4>
                          <Heart className='h-4 w-4 text-red-500' />
                        </div>
                        <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                          <div className='flex items-center gap-1'>
                            <Calendar className='h-3 w-3' />
                            <span>{formatDateTime(event.datetime)}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            <span>{event.ground?.location.city}</span>
                          </div>
                        </div>
                        {event.tags && (
                          <div className='mt-2 flex gap-1'>
                            {event.tags.slice(0, 3).map((tag: string) => (
                              <Badge key={tag} variant='secondary' className='text-xs'>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='text-right'>
                        <div className='font-medium'>
                          From{" "}
                          {formatCurrency(
                            Math.min(...(event.ticketTypes?.map((t: any) => t.price) || [0]))
                          )}
                        </div>
                        <Button size='sm' asChild>
                          <Link to={`/events/${event.id}`}>Book Now</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : // Fallback to featured events if no personalized recommendations
              recommendedEvents.length > 0 ? (
                <div className='space-y-4'>
                  <p className='mb-4 text-sm text-muted-foreground'>
                    Featured events you might enjoy:
                  </p>
                  {recommendedEvents.map((event: any) => (
                    <div
                      key={event.id}
                      className='flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <div className='flex items-center gap-2'>
                          <h4 className='font-medium'>{event.title}</h4>
                          <Star className='h-4 w-4 text-yellow-500' />
                        </div>
                        <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                          <div className='flex items-center gap-1'>
                            <Calendar className='h-3 w-3' />
                            <span>{formatDateTime(event.datetime)}</span>
                          </div>
                          <div className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            <span>{event.ground?.location.city}</span>
                          </div>
                        </div>
                        {event.tags && (
                          <div className='mt-2 flex gap-1'>
                            {event.tags.slice(0, 3).map((tag: string) => (
                              <Badge key={tag} variant='secondary' className='text-xs'>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='text-right'>
                        <div className='font-medium'>
                          From{" "}
                          {formatCurrency(
                            Math.min(...(event.ticketTypes?.map((t: any) => t.price) || [0]))
                          )}
                        </div>
                        <Button size='sm' asChild>
                          <Link to={`/events/${event.id}`}>Book Now</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Heart className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>
                    No recommendations yet. Book some events to get personalized suggestions!
                  </p>
                  <Button asChild>
                    <Link to='/events'>Explore Events</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Favorite Events */}
          {fanAnalytics?.favoriteEvents && fanAnalytics.favoriteEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Star className='h-5 w-5 text-yellow-500' />
                  Your Favorite Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {fanAnalytics.favoriteEvents.map((event: any) => (
                    <div key={event.eventId} className='flex items-center justify-between'>
                      <div>
                        <h4 className='font-medium'>{event.eventTitle}</h4>
                        <p className='text-sm text-muted-foreground'>
                          Booked {event.bookingCount} time(s)
                        </p>
                      </div>
                      <div className='text-right'>
                        <div className='text-sm font-medium'>
                          {formatCurrency(event.revenue)} spent
                        </div>
                        <div className='flex items-center gap-1'>
                          <Star className='h-3 w-3 text-yellow-500' />
                          <span className='text-xs text-muted-foreground'>
                            {event.occupancyRate?.toFixed(1) || 0}% filled
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='analytics' className='space-y-4'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {/* Booking History Analytics */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Target className='h-5 w-5' />
                  Booking Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookingStats && (
                  <div className='space-y-3'>
                    <div className='flex justify-between'>
                      <span>Total Bookings:</span>
                      <span className='font-medium'>{bookingStats.totalBookings}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Confirmed:</span>
                      <span className='font-medium text-green-600'>
                        {bookingStats.confirmedBookings}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Completed:</span>
                      <span className='font-medium text-blue-600'>
                        {bookingStats.completedBookings}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Cancelled:</span>
                      <span className='font-medium text-red-600'>
                        {bookingStats.cancelledBookings}
                      </span>
                    </div>
                    <div className='border-t pt-3'>
                      <div className='flex justify-between'>
                        <span>Success Rate:</span>
                        <span className='font-medium text-green-600'>
                          {(
                            (bookingStats.completedBookings /
                              Math.max(bookingStats.totalBookings, 1)) *
                            100
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Cancellation Rate:</span>
                        <span className='font-medium text-orange-600'>
                          {bookingStats.cancellationRate?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Spending Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <DollarSign className='h-5 w-5' />
                  Spending Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  <div className='flex justify-between'>
                    <span>Total Spent:</span>
                    <span className='font-medium text-green-600'>{formatCurrency(totalSpent)}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Average per Booking:</span>
                    <span className='font-medium'>{formatCurrency(avgBookingValue)}</span>
                  </div>
                  {!!fanAnalytics?.spendingPattern?.length && (
                    <div className='border-t pt-3'>
                      <h4 className='mb-2 text-sm font-medium'>By Pattern</h4>
                      {Object.entries(fanAnalytics.spendingPattern).map(
                        ([category, amount]: [string, any]) => (
                          <div key={category} className='flex justify-between text-sm'>
                            <span>{capitalizeFirst(category)}:</span>
                            <span className='font-medium'>{formatCurrency(amount)}</span>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fan Activity */}
          {fanAnalytics && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Your Fan Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                  {/* Favorite Events */}
                  {!!fanAnalytics.favoriteEvents?.length && (
                    <div className='col-span-1'>
                      <h4 className='mb-3 text-sm font-medium'>Favorite Events</h4>
                      <div className='space-y-2'>
                        {fanAnalytics.favoriteEvents.slice(0, 5).map((event, index) => (
                          <div
                            key={`${event.eventId}_${index}`}
                            className='flex justify-between text-sm'
                          >
                            <span>{capitalizeFirst(event.eventTitle)}</span>
                            <span className='font-medium'>{event.bookingCount} bookings</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Spending Pattern */}
                  {!!fanAnalytics.spendingPattern?.length && (
                    <div className='col-span-1'>
                      <h4 className='mb-3 text-sm font-medium'>Spending Pattern</h4>
                      <div className='space-y-2'>
                        {fanAnalytics.spendingPattern.slice(0, 5).map((entry, idx) => (
                          <div key={idx} className='flex justify-between text-sm'>
                            <span>
                              {entry.month} {entry.year}
                            </span>
                            <span className='font-medium'>Rs. {entry.revenue}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Booking Summary */}
                  <div className='col-span-1 md:col-span-2'>
                    <h4 className='mb-3 text-sm font-medium'>Booking Summary</h4>
                    <div className='grid grid-cols-2 gap-4 text-sm'>
                      <div className='flex flex-col'>
                        <span className='text-muted-foreground'>Total Bookings</span>
                        <span className='font-medium'>
                          {fanAnalytics.bookingHistory.totalBookings}
                        </span>
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-muted-foreground'>Completed Bookings</span>
                        <span className='font-medium'>
                          {fanAnalytics.bookingHistory.completedBookings}
                        </span>
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-muted-foreground'>Total Revenue</span>
                        <span className='font-medium'>
                          Rs. {fanAnalytics.bookingHistory.totalRevenue}
                        </span>
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-muted-foreground'>Avg Booking Value</span>
                        <span className='font-medium'>
                          Rs. {fanAnalytics.bookingHistory.averageBookingValue}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Upcoming Events */}
                  {!!fanAnalytics.upcomingEvents?.length && (
                    <div className='col-span-1 md:col-span-2'>
                      <h4 className='mb-3 text-sm font-medium'>Your Upcoming Events</h4>
                      <div className='space-y-2'>
                        {fanAnalytics.upcomingEvents.slice(0, 3).map((event, index) => (
                          <div key={event.id} className='flex justify-between text-sm'>
                            <span>{capitalizeFirst(event.title)}</span>
                            <span className='text-muted-foreground'>
                              {new Date(event.datetime).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {!!fanAnalytics.recommendations?.length && (
                    <div className='col-span-1 md:col-span-2'>
                      <h4 className='mb-3 text-sm font-medium'>Recommended for You</h4>
                      <div className='space-y-2'>
                        {fanAnalytics.recommendations.slice(0, 3).map(event => (
                          <div key={event.id} className='flex justify-between text-sm'>
                            <span>{capitalizeFirst(event.title)}</span>
                            <span className='text-muted-foreground'>
                              {event.ground?.location?.city || "Unknown Location"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Performance Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <TrendingUp className='h-5 w-5' />
                Your Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-2 gap-6 md:grid-cols-4'>
                {/* Total Bookings */}
                <div className='text-center'>
                  <div className='text-2xl font-bold text-blue-600'>
                    {bookingStats?.totalBookings || 0}
                  </div>
                  <p className='text-xs text-muted-foreground'>Total Bookings</p>
                </div>

                {/* Attendance Rate */}
                <div className='text-center'>
                  <div className='text-2xl font-bold text-green-600'>
                    {(
                      ((bookingStats?.completedBookings || 0) /
                        Math.max(bookingStats?.totalBookings || 1, 1)) *
                      100
                    ).toFixed(1)}
                    %
                  </div>
                  <p className='text-xs text-muted-foreground'>Attendance Rate</p>
                </div>

                {/* Avg Spent */}
                <div className='text-center'>
                  <div className='text-2xl font-bold text-purple-600'>
                    {formatCurrency(avgBookingValue)}
                  </div>
                  <p className='text-xs text-muted-foreground'>Avg Spent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FanDashboard;
